import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import { PaginatedResponse } from "@/shared/http";

import { CollectionRecord, CollectionResourceType } from "../CollectionRecord";

import { collectionQueryKeys } from "./collectionQueryKeys";

export interface UseCollectionsQueryOptions {
  orgId?: string;
  /** When set, restricts the result to collections holding this resource
   *  type (e.g. ``event`` collections for a create-events agent). */
  resourceType?: CollectionResourceType;
  /** When set, filters results to collections whose name contains this string (server-side). */
  nameFilter?: string;
  /** Collections to leave out of the result (server-side). */
  excludeIds?: string[];
  /** Field to sort by. Defaults to "created". */
  sortBy?: string;
  limit?: number;
}

/**
 * Query key for {@link useCollectionsQuery}. Exported so callers can
 * invalidate or prefetch the collections list without re-spelling the array.
 */
export function getQueryKey(options?: UseCollectionsQueryOptions) {
  return [
    ...collectionQueryKeys.lists(options?.orgId ?? ""),
    options?.resourceType,
    options?.nameFilter,
    options?.sortBy,
    options?.limit,
    options?.excludeIds,
  ];
}

/**
 * Org-scoped list of collections, newest first. Backs collection pickers
 * such as the agent-launch ``COLLECTION_ID`` variable input. Fetches a
 * single page (see ``CollectionService.searchCollections`` for the cap);
 * pickers fall back to free-text entry for ids beyond it.
 */
export function useCollectionsQuery(
  options?: UseCollectionsQueryOptions,
): UseQueryResult<PaginatedResponse<CollectionRecord>, Error> {
  const { collections } = useDomainServices();

  return useQuery<PaginatedResponse<CollectionRecord>, Error>({
    queryKey: getQueryKey(options),
    queryFn: ({ signal }) =>
      collections.searchCollections({
        orgId: options!.orgId!,
        resourceType: options?.resourceType,
        nameFilter: options?.nameFilter,
        excludeIds: options?.excludeIds,
        sortBy: options?.sortBy,
        limit: options?.limit,
        abortSignal: signal,
      }),
    enabled: options?.orgId !== undefined,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
